/**
 * EK-A15 — die EINE Stelle, die entscheidet, was eine Mindestpreis-Warnung sagt.
 *
 * 🔴 WARUM ES DIESE DATEI GIBT
 *
 * Vier Flaechen melden eine Unterschreitung: der Produktdialog, der
 * Markt-Preisueberblick, die Warnliste der Preisautomation und deren
 * Kopfzeile. Bis zum 2026-09-15 traf **jede von ihnen die Entscheidung
 * selbst** — vier Kopien derselben Bedingungen, in vier Schreibweisen.
 *
 * Drei Gate-Runden haben dreimal genau dieselbe Sorte Fehler gefunden, jedes
 * Mal in einer ANDEREN Kopie:
 *
 *   * der Stringvergleich statt `Number()` (Regel 16, Runde 1)
 *   * die fehlende Waehrungs- und Nullwache (Regel 16, Runde 1)
 *   * ein `else`, das jedes unbenannte Urteil als Unterschreitung ausgab
 *     (Regel 16, Runde 3) — **in zwei von drei Flaechen**, weil nur die
 *     dritte eine Probe hatte
 *
 * Das ist kein Sorgfaltsproblem, sondern ein Bauproblem: **wer vier Kopien
 * pflegt, repariert immer die, fuer die er gerade eine Probe hat.** Die
 * Entscheidung wohnt ab jetzt hier, einmal, und die Flaechen formulieren nur
 * noch.
 *
 * ⚠️ Was hier NICHT wohnt: der Wortlaut. Die vier Flaechen sprechen
 * verschieden ("Saved —", "Price updated —", "SKU · DE: …"), und das soll so
 * bleiben. Geteilt wird, WAS gilt, nicht WIE es klingt.
 */

export const URTEIL_UNBEKANNT = 'unbekannt';
export const URTEIL_UNTER = 'unter';
export const URTEIL_UNTER_PRODUKTBODEN = 'unter_produktboden';

const zahl = (v) => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

/** Ist der Betrag eine brauchbare Bodenangabe? `0` ist es NICHT.
 *
 * 🔴 `compute_min_price(0)` liefert im Backend ausdruecklich `0.00`. Ein
 * Mindestpreis von null ist kein Mindestpreis, sondern ein fehlender Wert,
 * der wie eine Erlaubnis aussieht. */
const gueltig = (v) => {
  const n = zahl(v);
  return n !== null && n > 0;
};

/**
 * Was gilt fuer diese Warnung? Die einzige Stelle, die das entscheidet.
 *
 * @param warnung  der `mindestpreis_warnung`-Block aus der Antwort, oder null
 * @param waehrungRueckfall  was gilt, wenn die Antwort keine Waehrung traegt.
 *   🔴 MUSS dieselbe Quelle sein, aus der die Flaeche auch ihre Zahlen
 *   formatiert — sonst rechnet die Wache gegen eine andere Waehrung als die
 *   Anzeige. Genau das ist in `MarketPricingOverview` passiert (Regel 15).
 */
export function bodenLage(warnung, waehrungRueckfall = 'EUR') {
  if (!warnung) return null;

  const waehrung = warnung.waehrung || waehrungRueckfall;
  const marktboden = zahl(warnung.mindestpreis);
  const produktboden = zahl(warnung.produktboden);
  // 🔴 NUR was auch gelesen wird. Eine erste Fassung reichte `betrag`,
  // `marktboden`, `produktboden` und `waehrung` mit heraus -- vier Felder, die
  // aussahen wie das Ergebnis der geteilten Stelle und die keine Flaeche je
  // gelesen hat (sie formatieren aus `warnung.*`). Das Regel-16-Gate hat sie
  // mutiert: alle 44 Proben blieben gruen. Ein Feld ohne Verbraucher ist eine
  // stille Zusicherung.
  const gemeinsam = { urteil: warnung.urteil };

  if (warnung.urteil === URTEIL_UNBEKANNT) {
    return { ...gemeinsam, art: URTEIL_UNBEKANNT,
             zeigtMarktboden: false, zeigtProduktboden: false, lang: false };
  }

  if (warnung.urteil === URTEIL_UNTER_PRODUKTBODEN) {
    // Der durchgesetzte Boden haelt, der kalkulatorische ist gerissen.
    // ⚠️ Den durchgesetzten nur nennen, wenn er eine echte Zahl ist: sonst
    // stuende dort "holds the market minimum of 0,00 €" — ein Boden von null,
    // der haelt.
    return { ...gemeinsam, art: URTEIL_UNTER_PRODUKTBODEN,
             zeigtMarktboden: gueltig(warnung.mindestpreis),
             zeigtProduktboden: gueltig(warnung.produktboden),
             lang: true };
  }

  if (warnung.urteil === URTEIL_UNTER) {
    // Der kalkulatorische Boden kommt als ZUSATZ dazu — aber nur, wenn er
    // etwas anderes sagt als der durchgesetzte, und nur in EUR: `p.min_price`
    // ist eine EUR-Zahl, dieselbe Begruendung wie im Backend.
    // 🔴 KEIN `gueltig(mindestpreis)` hier. Eine erste Fassung hatte es,
    // und es kostete genau den Fall, fuer den die Zahl gebraucht wird: bei
    // `mindestpreis = 0` und `produktboden = 10` verschwand der Zusatz -- die
    // einzige verwertbare Zahl im Satz. `zeigtMarktboden` haelt die 0,00
    // ohnehin draussen; diese Bedingung leistete nichts als den Verlust.
    //
    // Das Regel-15-Gate hat es ueber einen Differenzlauf gegen die alten vier
    // Kopien gefunden: von 1.050 Eingaben war das die EINZIGE Abweichung, die
    // keine verlangte Reparatur war. Und die Mutation darauf ueberlebte alle
    // 49 Proben -- deshalb steht die Probe jetzt daneben.
    const zusatz = (
      gueltig(warnung.produktboden)
      && produktboden !== marktboden
      && waehrung === 'EUR'
    );
    // 🔴 Die Nullwache gilt HIER GENAUSO. Eine erste Fassung setzte
    // `zeigtMarktboden: true` unbedingt -- und begruendete zwei Zeilen weiter
    // oben die Waehrungswache mit "heute unerreichbar, morgen nicht mehr".
    // Beides zusammen geht nicht: entweder der Fall zaehlt oder er zaehlt
    // nicht. Vom Regel-16-Gate als Widerspruch gemessen.
    //
    // ⚠️ Ueber den heutigen Erzeuger ist er unerreichbar:
    // `preisboden.py` liefert bei fehlendem Boden `kein_boden` statt `unter`,
    // und `b < 0` gaebe es nicht. Eine Messung, keine Eigenschaft.
    return { ...gemeinsam, art: URTEIL_UNTER,
             zeigtMarktboden: gueltig(warnung.mindestpreis),
             zeigtProduktboden: zusatz, lang: zusatz };
  }

  // 🔴 Alles andere: NICHT als Unterschreitung ausgeben.
  //
  // Bis zum 2026-09-15 fing hier ein `else` jedes unbenannte Urteil und
  // behauptete "is below the minimum price of X" — mit einer Zahl, die gar
  // nicht gerissen sein muss. Erreichbar wird das, sobald das Backend ein
  // sechstes Urteil einfuehrt, oder waehrend eines Deploy-Schiefstands.
  return { ...gemeinsam, art: 'flagged',
           zeigtMarktboden: false, zeigtProduktboden: false, lang: false };
}

/**
 * Wie lange die Meldung stehen bleibt, in Millisekunden.
 *
 * 🔴 Auch das ist eine ENTSCHEIDUNG und gehoert deshalb hierher. Zwei
 * Fassungen davor lag sie bei den Flaechen: erst als Textprobe auf den
 * Wortlaut einer anderen Datei (`/calculated minimum/.test(meldung)`), dann
 * als vierte eigene Herleitung, der die Waehrungswache fehlte.
 *
 * ⚠️ Was weiterhin NICHT gemessen wird: ob die Flaechen den Rueckgabewert
 * auch wirklich an `toast(..., { duration })` weiterreichen. Fuer
 * `ProductDetail` gibt es bis heute keine Probendatei.
 *
 * 🟢 Fuer `MarketPricingOverview` gibt es seit dem 2026-09-18 eine
 * (`components/__tests__/MarketPricingOverviewUnterMindestpreis.test.jsx`) --
 * und die Einschaetzung „eine Render-Probe waere fuer eine 51-KB-Komponente
 * unverhaeltnismaessig", die hier stand, hat NICHT getragen: sie kostete
 * einen Anlauf, keine `package.json`-Aenderung und keine Bewegung an den
 * Testflaechen-Zahlen ausser der Datei selbst. ⚠️ Sie deckt allerdings die
 * UNTERSCHREITUNGSZEILE, nicht die Anzeigedauer -- fuer `anzeigeDauer` bleibt
 * der Satz oben in beiden Flaechen woertlich wahr.
 *
 * 🔴 Das ist keine Kleinigkeit: das Regel-15-Gate hat sechs
 * Verdrahtungs-Mutationen je Flaeche gefahren. Am Dialog starben alle sechs,
 * an `PricingAutomationDashboard` vier -- an `MarketPricingOverview`
 * **keine einzige**. Die ENTSCHEIDUNG hier ist gepinnt; dass jene Flaeche sie
 * liest, ist es nicht. Genau diese Flaeche hat zwei Runden lang Fehler
 * getragen, die anderswo laengst behoben waren.
 */
export function anzeigeDauer(lage) {
  return lage && lage.lang ? 14000 : 8000;
}

/** Der Klammerzusatz fuer `flagged` — ohne "(undefined)" oder "(null)". */
export function artZusatz(lage) {
  return lage.urteil ? ` (${lage.urteil})` : '';
}

/**
 * Liegt eine EMPFEHLUNG unter dem KALKULATORISCHEN Mindestpreis?
 *
 * 🔴 Der Gegenstand ist ein ANDERER als bei `bodenLage`: dort ein bereits
 * geschriebener Preis mit dem `mindestpreis_warnung`-Block der Antwort, hier
 * ein VORSCHLAG, der noch nicht geschrieben ist. Dieselbe Zahl, anderer
 * Zeitpunkt.
 *
 * 🔴 `min_price` ist ueberall `p.min_price` (`backend/price_reco_sql.py`) --
 * der KALKULATORISCHE Mindestpreis. NICHT `market_min_price`
 * (`COALESCE(mp.min_price, p.min_price)`), das ist der DURCHGESETZTE, den
 * `bodenLage` als `warnung.mindestpreis` fuehrt. Zwei Zahlen, ein Wort -- die
 * Verwechslung hat das Regel-16-Gate am 2026-09-15 schon einmal gefunden.
 *
 * ⚠️ Gibt NUR zurueck, was auch gelesen wird (dieselbe Regel wie bei
 * `bodenLage`: ein Feld ohne Verbraucher ist eine stille Zusicherung).
 */
export function unterMindestpreisAusEmpfehlung(zeile) {
  if (!zeile) return null;
  // 🔴 WAEHRUNGSWACHE (Orchestrator-Entscheid G-1, 2026-09-18). NICHT zu
  // entfernen: `min_price` ist eine EUR-Zahl; `recommended_price` traegt die
  // Marktwaehrung. Im Backend verhindert `_vergleichbar`
  // (`pricing_automation.py`) genau diesen Vergleich, und die EINE Quelle jenes
  // Urteils (`price_reco_sql.py`) sagt in ihrem eigenen Docstring, dass es
  // davon genau eine Fassung geben soll. Ein zweiter Entscheider ohne Wache
  // waere derselbe Defekt in neu.
  //
  // Die Marke liefert `_build_market_recommendation_row` bereits mit; sie muss
  // nur durch `applyRecommendationData` durchgereicht werden (Aufgabe E).
  if (zeile.waehrung_nicht_vergleichbar) return null;
  const vorschlag = zahl(zeile.recommended_price);
  const boden = zahl(zeile.min_price);
  if (vorschlag === null || !gueltig(boden)) return null;
  return vorschlag < boden ? { mindestpreis: boden } : null;
}
